<template>
  <div>
    <div class="row mb-5 px-0">
      <div class="col-4">
        <div class="b-form__container">
          <label for="name">{{$t('CONTENT.assets.asset_component_name')}}:</label>
          <input
            data-cy="asset-component-name"
            type="text"
            id="name"
            class="b-input"
            name="name"
            v-model="assetComponent.name"/>
        </div>
      </div>
      <div class="col-4">
        <div class="b-form__container">
          <label for="externalId">{{$t('CONTENT.external_id')}}:</label>
          <input
            data-cy="asset-component-externalId"
            type="text"
            id="externalId"
            class="b-input"
            name="externalId"
            v-model="assetComponent.externalId"/>
        </div>
      </div>
    </div>
    <div v-if="!isSimplifiedAssetComponent">
    <div class="row px-0 mb-4">
      <div class="col-4">
        <div class="b-form__container">
          <checkbox class="b-checkbox" v-model="assetComponent.isAmortizable">{{$t('CONTENT.assets.isAmortizable')}}</checkbox>
        </div>
      </div>
      <div class="col-4">
        <div class="b-form__container" data-cy="asset-component-liquidationValue">
          <label>
            {{$t('CONTENT.assets.liquidation_value')}}:
            <span class="b-form__required">*</span>
          </label>
          <numeric v-bind:class="{ 'b-numeric__error': isFieldWithError('liquidationValue', assetComponent.liquidationValue) }"
            class="b-numeric" controls :min="0" :max="1000000000000" v-model="assetComponent.liquidationValue"></numeric>
        </div>
      </div>
    </div>
    <div class="row px-0">
      <div class="col-4">
        <div class="b-form__container" data-cy="asset-component-quantity">
          <label>
            {{$t('CONTENT.assets.leased_amount')}}:
            <span class="b-form__required">*</span>
          </label>
          <numeric v-bind:class="{ 'b-numeric__error': isFieldWithError('quantity', assetComponent.quantity) }" class="b-numeric" controls :min="0" :max="1000000000000" v-model="assetComponent.quantity"></numeric>
        </div>
      </div>
      <div class="col-4">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.unitOfMeasure')}}:
            <span class="b-form__required">*</span>
          </label>
          <cool-select :list="unitsOfMeasure"
                       option-value="code"
                       option-text="name"
                       :placeholder="$t('CONTENT.choose')"
                       :isError="isFieldWithError('unitOfMeasureCode', assetComponent.unitOfMeasureCode)"
                       v-model="assetComponent.unitOfMeasureCode"></cool-select>
        </div>
      </div>
    </div>
    <div class="row mt-5 px-0 b-form__row_type_flex b-form__row_type_flex-end">
      <div class="col-4">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.lease_start_date')}}:
            <span class="b-form__required">*</span>
          </label>
          <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('startDate', startDate) }" class="b-datepicker" v-model="startDate" :formatter="datepickerFormat" :disabled-date="maxDateForStartDateFn"></datepicker>
        </div>
      </div>
      <div class="col-4">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.lease_end_date')}}:
            <span class="b-form__required">*</span>
          </label>
          <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('endDate', endDate) }" class="b-datepicker" :disabled="isDisabledEndDate" v-model="endDate" :formatter="datepickerFormat" :disabled-date="minDateForEndDateFn"></datepicker>
        </div>
      </div>
      <div class="col-4 b-form__container_type_flex">
        <checkbox class="b-checkbox" id="isPermanent" v-model="isPermanent">{{$t('CONTENT.assets.perpetual')}}</checkbox>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.isPermanentHint')" icon="question"></dynamic-icon>
      </div>
    </div>
    <div class="row mt-5">
      <div class="col-8">
        <div class="b-form__container">
          <label for="externalId">{{$t('CONTENT.assets.end_date_commentary')}}:</label>
          <textarea
            type="text"
            id="endDateCommentary"
            class="b-textarea"
            name="endDateCommentary"
            v-model="assetComponent.endDateCommentary"
          />
        </div>
      </div>
    </div>
    <div class="mt-6" v-if="!contract.hasManualCharts && !isEvent">
      <h4 class="mb-2">{{$t('CONTENT.assets.options')}}</h4>
      <ExtensionOptionEvent :assetComponent="assetComponent" :form="assetComponent.extension" />
      <TerminationOptionEvent
        :assetComponent="assetComponent"
        :form="assetComponent.termination"
        :currencyCodes="currencyCodes"
      />
      <BuyoutOptionEvent
        :form="assetComponent.buyout"
        :currencyCodes="currencyCodes"
        :assetComponent="assetComponent"
      />
    </div>
    <div class="mt-6 b-contracts__component-wrap">
      <div class="mb-2 b-form__container_type_flex">
        <h4>{{$t('CONTENT.assets.devaluations')}}</h4>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.devaluationHint')" icon="question"></dynamic-icon>
      </div>
      <div v-for="(item, index) in assetComponent.devaluations" class="b-contracts__component-item" :key="index">
        <DevaluationsItem
          :isPopup=true
          :devaluationsItem="item"
          :devaluationsItemIndex="index"
          :devaluationsLength="assetComponent.devaluations.length"
          :assetComponent="assetComponent"
          @onDeleteDevaulation="onDeleteDevaulation"
        ></DevaluationsItem>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-4">
        <div class="b-form__container">
          <button
            type="button"
            class="b-btn"
            @click="addDevaluations"
            :disabled="isDisabledAddDevaluations"
          >
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
            {{$t('CONTENT.assets.add_devaluation')}}
          </button>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <div class="mb-2 b-form__container_type_flex">
        <h4>{{$t('CONTENT.assets.expenses')}}</h4>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.expensesHint')" icon="question"/>
      </div>
      <div v-for="(item, index) in assetComponent.expenses" :key="index">
        <ExpensesItem
          :isPopup=true
          :expensesItem="item"
          :expensesItemIndex="index"
          :expensesLength="assetComponent.expenses.length"
          :assetComponent="assetComponent"
          @onDeleteExpenses="onDeleteExpenses"/>
      </div>
    </div>
    <div class="row mt-4" :class="{'mb-6': isEvent}">
      <div class="col-4">
        <div class="b-form__container">
          <button
            type="button"
            class="b-btn"
            @click="addExpenses"
            :disabled="isDisabledAddExpenses">
              <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"/>
              {{$t('CONTENT.assets.add_expenses')}}
          </button>
        </div>
      </div>
    </div>
    <div v-if="isEvent || isNewAsset">
      <div class="row mt-4">
        <div class="col-8">
          <label>{{$t('CONTENT.contract.lessor')}}:</label>
        </div>
      </div>
      <div class="row py-4 px-4" v-if="assetLessors.length === 0">--</div>
      <div class="row pb-4" v-for="(assetLessor, i) in assetLessors" :key="assetLessor">
        <div class="col-8">
          <input type="text" class="b-input" :value="assetLessor" disabled/>
        </div>
        <div class="col1">
          <button type="button" class="b-btn b-btn_border_none ml-auto" @click="()=>removeAssetComponentLessor(i)" v-if="assetLessors.length > 1 || isEvent">
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="close"/>
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
  import dateConverter from "../../../utils/converters/date.converter";
  import DateWithoutTimeAPI from "../../../api/DateWithoutTimeAPI";
  import DevaluationsItem from "./DevaluationsItem";
  import ExpensesItem from "./ExpensesItem";
  import ExtensionOptionEvent from "../../eventRegistry/components/forms/options/ExtensionOptionEvent";
  import TerminationOptionEvent from "../../eventRegistry/components/forms/options/TerminationOptionEvent";
  import Termination from "../../../models/options/Termination";
  import ExtensionOption from "../../../models/contract/ExtensionOption";
  import BuyoutOptionEvent from "../../eventRegistry/components/forms/options/BuyoutOptionEvent";
  import {mapGetters} from 'vuex';
  import BuyoutOption from "../../../models/contract/BuyoutOption";
  import datepickerFormat from '@/mixins/datepickerFormat.mixin';
  import ContractAggregate from '../../../models/contract/ContractAggregate';
  import LessorComponentShares from '../models/lessorComponentShares';

	export default {
	  components: {
      DevaluationsItem,
      ExpensesItem,
      ExtensionOptionEvent,
      TerminationOptionEvent,
      BuyoutOptionEvent
    },
    mixins: [datepickerFormat],
	  props: {
      isEvent: {
        type: Boolean,
      },
      isSimplifiedAssetComponent: {
        type: Boolean,
      },
      assetComponent: {
        required: true,
      },
      unitsOfMeasure: {
        type: Array,
        required: true,
      },
      contract: {
        type: Object,
        required: true,
      },
      validationErrors: {
        type: Array
      }
    },
    data() {
      return {
        maxDateForStartDate: null,
        minDateForEndDate: null,
        extension: null,
        isNewAsset: false,
      }
    },
    created() {
      this.isNewAsset = this.assetComponent.assetId === null
      if(this.isEvent || this.isNewAsset) {
        this.assetComponent.lessorComponentShares = this.currentLessors
          .map(lessor => LessorComponentShares.createNew(lessor.idInDB, this.assetComponent.startDate, this.assetComponent.endDate))
      }

      if(this.isEvent) return;

      if (this.assetComponent.extension !== null) {
        let extension = this.assetComponent.extension;
        this.$set(extension, 'isExtension', true);
        this.assetComponent.extension = extension;
      } else {
        let extension = ExtensionOption.createNew();
        this.$set(extension, 'isExtension', false);
        this.assetComponent.extension = extension;
      }

      if (this.assetComponent.termination !== null) {
        let termination = this.assetComponent.termination;
        this.$set(termination, 'isTermination', true);
        this.assetComponent.termination = termination;
      } else {
        let termination = Termination.createNew();
        this.$set(termination, 'isTermination', false);
        this.assetComponent.termination = termination;
      }

      if (this.assetComponent.buyout !== null) {
        let buyout = this.assetComponent.buyout;
        this.$set(buyout, 'isBuyout', true);
        this.assetComponent.buyout = buyout;
      } else {
        let buyout = BuyoutOption.createNew();
        this.$set(buyout, 'isBuyout', false);
        this.assetComponent.buyout = buyout;
      }
    },
    watch: {
      isSeveralAssetComponents(val) {
        if(val === false) {
          this.name = null;
          this.externalId = null;
        }
      },
      'assetComponent.termination.date'(val) {
        if(val === null) {
          this.assetComponent.endDate = this.assetComponent.endDateInitial;
        }
      },
      'assetComponent.extension.date'(val) {
        if(val === null) {
          this.assetComponent.endDate = this.assetComponent.endDateInitial;
        }
      }
    },
    computed: {
      ...mapGetters(["getCounterpartiresForContract"]),
      ...mapGetters(["allCounterparties"]),
      ...mapGetters(["getCurrencyCode"]),
      currencyCodes() {
        return this.getCurrencyCode;
      },
      currentLessors() {
        return this.allCounterparties
          .filter(counterparty => this.contract.contract.lessorCounterpartyIds.includes(counterparty.idInDB))
      },
      isDisabledAddDevaluations() {
        for (let i = 0; i < this.assetComponent.devaluations.length; i++) {
          if (
            this.assetComponent.devaluations[i].startDate === null ||
            this.assetComponent.devaluations[i].sum === null
          ) {
            return true;
          }
        }
        return false;
      },
      isDisabledAddExpenses() {
        for (let i = 0; i < this.assetComponent.expenses.length; i++) {
          if (
            this.assetComponent.expenses[i].startDate === null ||
            this.assetComponent.expenses[i].sum === null
          ) {
            return true;
          }
        }

        return false;
      },
      startDate: {
        get() {
          if (this.assetComponent.startDate === null) {
            return null;
          } else {
            return dateConverter.convertToDate(this.assetComponent.startDate);
          }
        }, set(newVal) {
          if (newVal !== null) {
            this.minDateForEndDate = newVal;
            this.assetComponent.startDate = dateConverter.convertToString(newVal);
            if (
              this.assetComponent.startDate !== null &&
              this.assetComponent.startDate !== "" &&
              this.assetComponent.isPermanent
            ) {
              this.getDateWithPermanent();
            }
          } else {
            this.assetComponent.startDate = null;
          }
        }
      },
      endDate: {
        get() {
          if (this.assetComponent.endDate === null) {
            return null;
          } else {
            return dateConverter.convertToDate(this.assetComponent.endDate);
          }
        }, set(newVal) {
          if(newVal !== null) {
            this.maxDateForStartDate = newVal;
            this.assetComponent.endDate = dateConverter.convertToString(newVal);
            this.assetComponent.endDateInitial = dateConverter.convertToString(newVal);
            if (
              this.assetComponent.startDate !== null &&
              this.assetComponent.startDate !== "" &&
              this.isPermanent
            ) {
              this.getDateWithPermanent();
            }
          } else {
            this.assetComponent.endDate = null;
            this.assetComponent.endDateInitial = null;
          }
        }
      },
      isDisabledEndDate() {
        return !!this.assetComponent.isPermanent;
      },
      isPermanent: {
        get() {
          if (this.assetComponent.isPermanent !== null) {
            return this.assetComponent.isPermanent;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.assetComponent.isPermanent = newVal;

          if (
            this.assetComponent.startDate !== null &&
            this.assetComponent.startDate !== "" &&
            newVal
          ) {
            this.getDateWithPermanent();
          }
        }
      },
      assetLessors() {
        return this.assetComponent.lessorComponentShares
          .map(lessorComponentShare => {
            const lessor = this.getCounterpartiresForContract
              .find(counterparty => counterparty.id === lessorComponentShare.lessorId)
            if(lessor === undefined) return
            return lessor.value
          }).filter(counterparty => counterparty !== undefined)
      },
    },
    methods: {
      minDateForEndDateFn(date) {
        if(this.minDateForEndDate !== null) {
          return date < this.minDateForEndDate;
        }
      },
      maxDateForStartDateFn(date) {
        if(this.maxDateForStartDate !== null) {
          return date > this.maxDateForStartDate;
        }
      },
      async getDateWithPermanent() {
        const convertedCurrentAssetComponentStartDate = encodeURIComponent(
          this.assetComponent.startDate
        );
        let newDate = await new DateWithoutTimeAPI().addYearsBasedOnPerpetuity(
          convertedCurrentAssetComponentStartDate
        );
        this.assetComponent.endDate = newDate;
        this.assetComponent.endDateInitial = newDate;
      },
      addDevaluations() {
        let item = {
          id: null,
          sum: null,
          startDate: null
        };

        this.assetComponent.devaluations.push(item);
      },
      addExpenses() {
        let item = {
          id: null,
          type: null,
          sum: null
        };

        this.assetComponent.expenses.push(item);
      },
      onDeleteDevaulation(itemIndex) {
        this.assetComponent.devaluations.splice(itemIndex, 1);
      },
      onDeleteExpenses(itemIndex) {
        this.assetComponent.expenses.splice(itemIndex, 1);
      },
      isFieldWithError(field, value) {
        return !value && this.validationErrors && this.validationErrors.includes(field);
      },
      removeAssetComponentLessor(idx) {
        this.assetComponent.lessorComponentShares.splice(idx, 1)
      }
    }
  }
</script>

